import { env } from '@/lib/env';
import i18next from 'i18next';

/** Idioma ativo da interface: o servidor responde (erros, notificações, Hexis) neste idioma. */
export const localeHeaders = (): Record<string, string> => (i18next.language ? { 'Accept-Language': i18next.language } : {});

// Camada de comunicação única (SDK interno). Substitui o api-client.js legacy.
// Prefixo canónico /api/v1. Token de sessão injetado por getToken (Clerk).
const BASE = `${env.apiOrigin}/api/v1`;

type TokenGetter = () => Promise<string | null>;
let tokenGetter: TokenGetter = async () => null;

/** Ligado uma vez pelo AuthProvider (Clerk). */
export function setTokenGetter(fn: TokenGetter) {
  tokenGetter = fn;
}

/** Base canónica da API (/api/v1). Para quem precisa de fetch cru (SSE streaming). */
export const API_BASE = BASE;

/** Token de sessão Clerk atual — a MESMA fonte que o http usa. Para fetch cru
 *  (streams SSE) que não passa pelo request() acima. Nunca ler de localStorage:
 *  o legado que guardava JWT no localStorage foi extinto no cutover. */
export function getAuthToken(): Promise<string | null> {
  return tokenGetter();
}

// A impersonação auditada (/access/impersonation) saiu da app de produto: é uma
// ferramenta da equipa e pertence à consola HQ. Aqui só viaja o token de sessão.
function identityHeaders(token: string | null): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Rotas PESSOAIS: sem sessão a API só pode responder 401. Em vez de gastar a
// viagem (e sujar a consola/monitorização com 401s «normais» de cada convidado),
// falha-se localmente com o mesmo ApiError. Quando o Clerk assenta como
// autenticado, o TokenBridge invalida as queries e elas repetem já com token.
const PERSONAL = /^\/(users\/me|access\/me|notifications|subscriptions\/me|wallet\/me)(\/|\?|$)/;

// ── Robustez de rede ──
// Sem timeout, um pedido pendurado deixava o ecrã em «A carregar» para sempre (e a fila
// offline presa). Agora: 20 s por pedido (60 s em uploads) e UM retry, só em GET (idempotente)
// e só para falhas transitórias (rede, timeout, 502/503/504). Escritas NUNCA são repetidas
// aqui: duplicar um POST (pagamento, ligação, mensagem) é pior do que mostrar o erro — a fila
// offline é que repete, com chave de idempotência.
const TIMEOUT_MS = 20_000;
const UPLOAD_TIMEOUT_MS = 60_000;
const TRANSIENT = new Set([502, 503, 504]);

async function fetchTimed(url: string, init: RequestInit, ms: number, path: string): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  const signal = init.signal ? AbortSignal.any([init.signal, ctrl.signal]) : ctrl.signal;
  try {
    return await fetch(url, { ...init, signal });
  } catch (err) {
    if (ctrl.signal.aborted && !init.signal?.aborted) throw new ApiError(408, `Tempo esgotado em ${path}`);
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await tokenGetter();
  if (!token && PERSONAL.test(path)) throw new ApiError(401, `Sem sessão para ${path}`);
  const doFetch = () => fetchTimed(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...localeHeaders(),
      ...identityHeaders(token),
      ...init.headers,
    },
  }, TIMEOUT_MS, path);
  const idempotent = !init.method || init.method === 'GET';
  let res: Response;
  try {
    res = await doFetch();
    if (idempotent && TRANSIENT.has(res.status)) { await new Promise((r) => setTimeout(r, 400)); res = await doFetch(); }
  } catch (err) {
    if (!idempotent || init.signal?.aborted) throw err;
    await new Promise((r) => setTimeout(r, 400));
    res = await doFetch();
  }

  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => undefined);
    }
    throw new ApiError(res.status, `API ${res.status} em ${path}`, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Upload multipart (avatar, media). NÃO define Content-Type — o browser
 *  gera o boundary. Reutiliza o mesmo token de sessão do request JSON. */
async function upload<T>(path: string, file: File, field = 'file'): Promise<T> {
  const token = await tokenGetter();
  const fd = new FormData();
  fd.append(field, file);
  const res = await fetchTimed(`${BASE}${path}`, {
    method: 'POST',
    headers: { ...localeHeaders(), ...identityHeaders(token) },
    body: fd,
  }, UPLOAD_TIMEOUT_MS, path);
  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { body = await res.text().catch(() => undefined); }
    throw new ApiError(res.status, `API ${res.status} em ${path}`, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const http = {
  get: <T>(path: string) => request<T>(path),
  upload,
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
